import { Ref, onBeforeUnmount } from 'vue';
// https://github.com/zxing-js/library
import { BrowserMultiFormatReader, Result, NotFoundException, DecodeHintType } from '@zxing/library';

export function useScanner(video: Ref<HTMLVideoElement | null>) {
  let barcodeDetector: any = null;
  let isScanning = false;
  let animationFrameId: number | null = null;

  async function getBarcodeDetector() {
    if (barcodeDetector) {
      return barcodeDetector;
    }
    if ('BarcodeDetector' in window) {
      barcodeDetector = new window.BarcodeDetector();
      return barcodeDetector;
    }
    return null;
  }

  async function decodeByBarcodeDetector(barcodeDetector: any): Promise<string> {
    const capture = (cb: (e:any, val: string) => void) => {
      const loop = async () => {
        if (!video.value || !isScanning) {
          return;
        }

        if (video.value.readyState !== video.value.HAVE_ENOUGH_DATA) {
          animationFrameId = window.requestAnimationFrame(loop);
          return;
        }

        try {
          const barcodes = await barcodeDetector.detect(video.value);
          if (barcodes.length) {
            cb(null, barcodes[0].rawValue);
            return;
          }
          animationFrameId = window.requestAnimationFrame(loop);
        } catch (e: any) {
          // 如果是视频未准备好的错误，继续尝试
          if (e.message?.includes('invalid element or state')) {
            animationFrameId = window.requestAnimationFrame(loop);
            return;
          }
          cb(e, '');
        }
      };
      loop();
    };

    if (animationFrameId) {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    return new Promise((resolve, reject) => {
      capture((e: any, val: string) => {
        if (e) {
          return reject(e);
        }
        resolve(val);
      });
    });
  }

  let zxingCodeReader: BrowserMultiFormatReader | null = null;
  function getZXingCodeReader() {
    if (zxingCodeReader) {
      return zxingCodeReader;
    }
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    hints.set(DecodeHintType.PURE_BARCODE, false);
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      'EAN_13',     // 最常用的商品条码
      'EAN_8',      // 小包装商品条码
      'UPC_A',      // 北美商品条码
      'UPC_E',      // 北美小包装商品条码
      'CODE_128',   // 物流包装常用
      'QR_CODE'     // 保留二维码支持
    ]);
    zxingCodeReader = new BrowserMultiFormatReader(hints);
    return zxingCodeReader;
  }

  async function decodeByZXingCodeReader(codeReader: BrowserMultiFormatReader): Promise<string> {
    if (!video.value) {
      return '';
    }
    try {
      const result: Result = await codeReader.decodeFromVideoElement(video.value);
      return result.getText();
    } catch (e) {
      if (e instanceof NotFoundException) {
        return '';
      }
      throw e;
    }
  }

  async function decode(): Promise<string> {
    const barcodeDetector = await getBarcodeDetector();
    if (barcodeDetector) {
      return decodeByBarcodeDetector(barcodeDetector);
    }
    // 不支持wasm 和 script module退回zxing
    return decodeByZXingCodeReader(getZXingCodeReader());
  }

  async function start() {
    if (!video.value) {
      // eslint-disable-next-line no-console
      console.warn('video is not found');
      return '';
    }
    // 先确保清理之前的扫描
    stop();
    isScanning = true;
    const result = await decode();
    stop();
    return result;
  }

  function stop() {
    isScanning = false;
    if (animationFrameId) {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    if (zxingCodeReader) {
      zxingCodeReader.reset();
      zxingCodeReader = null;
    }
    if (barcodeDetector) {
      barcodeDetector = null;
    }
  }

  onBeforeUnmount(stop);

  return {
    start,
    stop,
  };
}
